import {formatCurrency} from '@angular/common';
import {Injectable} from '@angular/core';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {Store} from '@ngrx/store';
import {
  getAutoOrderOrders,
  removeFailedItemsAndRetryAutoOrder,
  setAutoOrderChipStatus,
  setRetryAutoOrder,
} from '../store/auto-ordering.actions';
import {
  IAutoOrderFirestoreOrderErrorMessage,
  IAutoOrderOrder,
  IAutoOrderOrderPreparedInfoDetails,
} from '../models';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IModalDismissResult, ModalControllerService} from '../../../shared/shared-services/overlay/modal-controller.service';
import {setNavigation, setSelectedStore} from '../../user-access/user-stores-store/user-store.actions';
import {NavigationPageEnum, NavigationTabEnum} from '../../user-access/models/navigations.enums';
import {IStore} from '../../../shared/shared-models/store/store';
import {setGlobalModalVisibility} from '../../user-access/user-store/user.actions';
import {
  ISharedModalBasic,
  ISharedModalBasicResponse,
} from '../../../shared/shared-models/modals/shared-modal-basic';
import {
  SharedModalBasicComponent,
} from '../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {
  AutoOrderOrderErrorHumanReadableEnum,
  AutoOrderOrderItemErrorHumanReadableEnum,
} from '../models/auto-order-order-error.enum';
import {Ascii} from '../../../shared/shared-models/global/ascii.enum';
import {IUser} from '../../user-access/models';
import {getDownloadUrlFromStorage} from '../../core/store-shared/shared.actions';
import {updateNotification} from '../../notifications/store/notification.actions';
import {INotification} from '../../notifications/models';
import {buildAutoOrderNotificationErrorDescription, buildAutoOrderNotificationItemErrorEntries, resolveAutoOrderNotificationItemErrors} from '../../notifications/utils/notification-auto-order-error.utils';
import {IAutoOrderNotificationItemErrorEntry} from '../../notifications/models/notification-auto-order-item-error.model';
import {wouldPartialRetryEmptyOrder} from '../utils/auto-order-partial-retry.utils';

export type AutoOrderFailureType = 'EMAIL' | 'IQ' | 'PDF' | 'STATUS';

export interface IAutoOrderFailureModalOptions {
  notification?: INotification;
  storeId?: string;
  user?: IUser;
  viewOnly?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AutoOrderStatusService {

  constructor(
    private readonly store: Store,
    private readonly modalController: ModalControllerService,
  ) {
  }

  getStatusColour(
    autoOrder: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): IconColourEnum {
    switch (autoOrder[statusProperty]) {
      case 'READY':
        return IconColourEnum.primary;
      case 'PENDING':
      case 'PREPARED':
        return IconColourEnum.yellow;
      case 'EMPTY':
      case 'SKIP':
      case 'SENT':
      case 'SUCCESS':
        return IconColourEnum.green;
      case 'DELETING':
      case 'FAILED':
        return IconColourEnum.red;
      default:
        return IconColourEnum.white;
    }
  }

  getStatusToolTip(
    autoOrder: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): string {
    switch (autoOrder[statusProperty]) {
      case 'READY':
      case 'PENDING':
      case 'PREPARED':
      case 'EMPTY':
      case 'SKIP':
      case 'SENT':
      case 'SUCCESS':
        return autoOrder[statusProperty];
      case 'DELETING':
        return autoOrder[statusProperty];
      case 'FAILED':
        if (statusProperty === 'emailStatus') {
          return 'Generating Email Failed - Click here to view';
        }
        if (statusProperty === 'pdfStatus') {
          return 'PDF Generation Failed - Click here to view';
        }
        if (statusProperty === 'iqStatus') {
          return 'IQ Failed - Click here to view';
        }
        return autoOrder[statusProperty];
      default:
        return autoOrder[statusProperty];
    }
  }

  getStatusIcon(
    autoOrder: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): IconsEnum {
    let icon = IconsEnum.sync;
    switch (autoOrder[statusProperty]) {
      case 'READY':
        if (statusProperty === 'emailStatus') icon = IconsEnum.emailCheckOutline;
        if (statusProperty === 'pdfStatus') icon = IconsEnum.pdfBox;
        if (statusProperty === 'iqStatus') icon = IconsEnum.logoIQ;
        if (statusProperty === 'status') icon = IconsEnum.receiptTextOutline;
        break;
      case 'PENDING':
        if (statusProperty === 'emailStatus') icon = IconsEnum.emailSyncOutline;
        break;
      case 'SKIP':
        if (statusProperty === 'emailStatus') icon = IconsEnum.closeCircle;
        break;
      case 'SENT':
        if (statusProperty === 'emailStatus') icon = IconsEnum.emailFastOutline;
        break;
      case 'FAILED':
        if (statusProperty === 'emailStatus') icon = IconsEnum.emailAlertOutline;
        if (statusProperty === 'pdfStatus') icon = IconsEnum.pdfBox;
        if (statusProperty === 'iqStatus') icon = IconsEnum.logoIQ;
        if (statusProperty === 'status') icon = IconsEnum.receiptTextRemoveOutline;
        break;
      case 'SUCCESS':
        if (statusProperty === 'pdfStatus') icon = IconsEnum.download;
        if (statusProperty === 'iqStatus') icon = IconsEnum.logoIQ;
        if (statusProperty === 'status') icon = IconsEnum.receiptTextCheckOutline;
        break;
      case 'EMPTY':
        if (statusProperty === 'status') icon = IconsEnum.receiptOutline;
        break;
      case 'PREPARED':
        if (statusProperty === 'status') icon = IconsEnum.receiptTextEditOutline;
        break;
      case 'DELETING':
        if (statusProperty === 'status') icon = IconsEnum.deleteClockOutline;
        break;
      case 'CREATED':
      case 'SUBMITTED':
        break;
    }
    return icon;
  }

  /**
   * Handles status icon clicks from the notifications panel.
   * Shows error details for failed steps; downloads PDF on success.
   */
  onNotificationStatusIconClick(
    autoOrder: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
    options?: IAutoOrderFailureModalOptions,
  ): void {
    const status = autoOrder[statusProperty];
    if (status === 'FAILED') {
      const failureType: AutoOrderFailureType | null = this.mapStatusPropertyToFailureType(statusProperty);
      if (failureType) {
        void this.showOrderFailureModal(autoOrder, failureType, options);
      }
      return;
    }
    if (status === 'SUCCESS' && statusProperty === 'pdfStatus') {
      this.store.dispatch(getDownloadUrlFromStorage({
        storage: {
          document: autoOrder.orderId,
          extension: 'pdf',
          folder: 'orders',
        },
      }));
    }
  }

  /**
   * Builds a short single-line summary of the order error for inline display.
   */
  getOrderErrorSummary(order: IAutoOrderOrder, notificationResponse?: string): string {
    if (order.error) {
      const err: IAutoOrderFirestoreOrderErrorMessage = order.error;
      if (typeof err === 'string') {
        return err;
      }
      return `${AutoOrderOrderErrorHumanReadableEnum[err.error]} - ${err.desc}`;
    }
    if (order.errorMsg) {
      if (typeof order.errorMsg === 'string') {
        return order.errorMsg;
      }
      return order.errorMsg.message || order.errorMsg.code || 'An error occurred';
    }
    if (notificationResponse) {
      return notificationResponse;
    }
    return 'An error occurred while processing this order';
  }

  /**
   * Builds the detailed error description lines for the failure modal.
   */
  buildOrderErrorDescription(order: IAutoOrderOrder): string[] {
    const errorDisplay: string[] = [];
    const items: { [code: string]: IAutoOrderOrderPreparedInfoDetails } | undefined = order.preparedInfo?.preparedItems;

    if (order.error) {
      const err: IAutoOrderFirestoreOrderErrorMessage = order.error;
      if (typeof err === 'string') {
        errorDisplay.push(err);
        errorDisplay.push(Ascii.EmptySpace);
      } else {
        errorDisplay.push(`${AutoOrderOrderErrorHumanReadableEnum[err.error]} - ${err.desc}`);
        errorDisplay.push(Ascii.EmptySpace);

        if (err.itemErrors && Object.keys(err.itemErrors).length > 0) {
          errorDisplay.push('The following errors occured on specific items on your order.');
          errorDisplay.push(Ascii.EmptySpace);

          Object.entries(err.itemErrors).forEach(([itemCode, er]) => {
            errorDisplay.push(`[${AutoOrderOrderItemErrorHumanReadableEnum[er.error]}] ${er.desc}`);
            if (items?.[itemCode]) {
              errorDisplay.push(
                `${itemCode} - ${items[itemCode].desc} - ${items[itemCode].qty} - ${formatCurrency(items[itemCode].price, 'en-ZA', 'R', 'ZAR', '1.2-2')}`,
              );
            }
            errorDisplay.push(Ascii.EmptySpace);
          });
        }
      }
      errorDisplay.push('Please open the order and correct these issues, you can then place this order again by pressing retry.');
      errorDisplay.push(Ascii.EmptySpace);
    }

    if (order.errorMsg) {
      if (typeof order.errorMsg === 'string') {
        errorDisplay.push(order.errorMsg);
      } else {
        if (order.errorMsg.code) {
          errorDisplay.push(`Error: ${order.errorMsg.code}`);
        }
        if (order.errorMsg.message) {
          errorDisplay.push(`Message: ${order.errorMsg.message}`);
        }
      }
      errorDisplay.push(Ascii.EmptySpace);
    }

    return errorDisplay;
  }

  /**
   * Builds failure description from notification item errors and/or order document errors.
   */
  buildFailureDescription(order: IAutoOrderOrder, notification?: INotification): string[] {
    const notificationDescription: string[] = buildAutoOrderNotificationErrorDescription(notification, order);
    if (notificationDescription.length > 0) {
      return notificationDescription;
    }
    return this.buildOrderErrorDescription(order);
  }

  /**
   * Opens a read-only modal with auto-order failure details.
   */
  async showOrderErrorViewModal(
    order: IAutoOrderOrder | undefined,
    notification?: INotification,
  ): Promise<void> {
    if (!order) {
      const description: string[] = buildAutoOrderNotificationErrorDescription(notification, undefined);
      if (description.length === 0 && !notification?.response) {
        return;
      }
      const componentProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Close',
        buttonSecondary: false,
        buttonSecondaryText: '',
        modalTitle: 'Auto Order Error',
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: IconColourEnum.red,
        contextDescription: description.length > 0 ? description : [notification?.response ?? ''],
      };
      const modalController = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps,
        cssClass: ['shared-basic-modal-css-medium'],
      });
      await modalController.present();
      return;
    }
    await this.showOrderFailureModal(order, this.getPrimaryFailureType(order), {
      notification,
      viewOnly: true,
    });
  }

  /**
   * Retries a failed auto order.
   */
  retryOrder(order: IAutoOrderOrder, options?: IAutoOrderFailureModalOptions): void {
    if (options?.notification && !options.notification.retried) {
      this.store.dispatch(updateNotification({
        notification: {...options.notification, retried: true},
      }));
    }
    this.store.dispatch(setRetryAutoOrder({order}));
  }

  /**
   * Shows a confirmation modal listing items to remove, then retries without them.
   */
  async confirmAndRetryOrderWithoutFailedItems(
    order: IAutoOrderOrder,
    options: IAutoOrderFailureModalOptions,
    codesToRemove: string[],
    itemEntries?: IAutoOrderNotificationItemErrorEntry[],
  ): Promise<void> {
    if (codesToRemove.length === 0) {
      return;
    }

    if (wouldPartialRetryEmptyOrder(order, codesToRemove)) {
      await this.showAllItemsFailedModal();
      return;
    }

    const entries: IAutoOrderNotificationItemErrorEntry[] = itemEntries ?? buildAutoOrderNotificationItemErrorEntries(
      resolveAutoOrderNotificationItemErrors(options.notification, order),
    ).filter((entry: IAutoOrderNotificationItemErrorEntry) => codesToRemove.includes(entry.stockCode));

    const itemLines: string[] = entries.map(
      (entry: IAutoOrderNotificationItemErrorEntry) => `${entry.stockCode}${entry.desc ? ` — ${entry.desc}` : ''}`,
    );

    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Remove items & retry',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      buttonSecondary: false,
      buttonSecondaryText: '',
      modalTitle: 'Remove failed items',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'The following items will be removed from the order before retrying:',
      contextDescription: [
        ...itemLines,
        Ascii.EmptySpace,
        'These items will be permanently removed from this order. The order will then be resubmitted without them.',
      ],
    };

    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-larger'],
    });
    await modalController.present();
    const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await modalController.onDidDismiss();
    if (modalResponse?.data?.buttonPressed === 'button-accept') {
      this.retryOrderWithoutFailedItems(order, options, codesToRemove);
    }
  }

  async showAllItemsFailedModal(): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'OK',
      buttonSecondary: false,
      buttonSecondaryText: '',
      modalTitle: 'Cannot retry without failed items',
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: 'All items in this order have failed.',
      contextDescription: [
        'Removing the failed items would leave no items to submit, so this order cannot be retried without them.',
        Ascii.EmptySpace,
        'Please contact Techodactyl Support to resolve the errors on this order.',
      ],
    };

    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-medium'],
    });
    await modalController.present();
    await modalController.onDidDismiss();
  }

  /**
   * Removes failed items from an order and retries submission.
   */
  retryOrderWithoutFailedItems(
    order: IAutoOrderOrder,
    options: IAutoOrderFailureModalOptions,
    codesToRemove: string[],
  ): void {
    const storeId: string | undefined = options.storeId ?? options.notification?.storeId;
    const orderId: string | undefined = order.orderId ?? options.notification?.event;
    if (!storeId || !orderId || codesToRemove.length === 0) {
      return;
    }
    if (wouldPartialRetryEmptyOrder(order, codesToRemove)) {
      return;
    }
    if (options.notification && !options.notification.retried) {
      this.store.dispatch(updateNotification({
        notification: {...options.notification, retried: true},
      }));
    }
    this.store.dispatch(removeFailedItemsAndRetryAutoOrder({
      storeId,
      orderId,
      codesToRemove,
      order: {...order, orderId},
    }));
  }

  /**
   * Opens a modal showing the failure details for an auto order step.
   */
  async showOrderFailureModal(
    order: IAutoOrderOrder,
    failedOn: AutoOrderFailureType,
    options?: IAutoOrderFailureModalOptions,
  ): Promise<void> {
    const user: IUser | undefined = options?.user;
    const notification: INotification | undefined = options?.notification;
    const viewOnly: boolean = options?.viewOnly === true;
    const canRetry: boolean = !viewOnly && !notification?.retried;
    const failingItemCodes: string[] = buildAutoOrderNotificationItemErrorEntries(
      resolveAutoOrderNotificationItemErrors(notification, order),
    ).map((entry) => entry.stockCode);
    const isRetryableFailure: boolean = failedOn === 'IQ' || failedOn === 'STATUS';
    const canRetryOrder: boolean = canRetry && isRetryableFailure;
    const canRetryWithoutFailedItems: boolean = canRetry
      && failingItemCodes.length > 0
      && isRetryableFailure
      && !wouldPartialRetryEmptyOrder(order, failingItemCodes);

    let title = '';
    let contextHeading = '';
    let buttonAcceptText = '';
    let buttonSecondaryText = '';
    let additionalText: string[] = [];
    switch (failedOn) {
      case 'EMAIL':
        title = 'Sending Email Failed';
        contextHeading = 'NOTE: An error occurred with the email.';
        break;
      case 'IQ':
        title = 'Failure on IQ';
        contextHeading = 'NOTE: A communication error occurred.';
        buttonAcceptText = canRetryOrder ? 'Retry Order' : '';
        buttonSecondaryText = canRetryWithoutFailedItems ? 'Retry without failed items' : '';
        break;
      case 'PDF':
        title = 'Failure on PDF Generation';
        contextHeading = 'NOTE: Generating the PDF has failed.';
        break;
      case 'STATUS':
        title = 'Failure on Generating Order';
        contextHeading = 'NOTE: Generating the Order has failed.';
        buttonAcceptText = canRetryOrder ? 'Retry Order' : '';
        buttonSecondaryText = canRetryWithoutFailedItems ? 'Retry without failed items' : '';
        additionalText = canRetryOrder
          ? [
            'Please fix all errors on the order.',
            Ascii.EmptySpace,
            'Once fixed, you will be able to retry sending the order.',
            'Please contact Techodactyl Support when you retry an order.',
          ]
          : notification?.retried
            ? [
              'This order has already been retried.',
              Ascii.EmptySpace,
            ]
            : [];
        break;
    }

    const errorDisplay: string[] = this.buildFailureDescription(order, notification);
    const retryText: string[] = [
      user && user.id !== order.userId
        ? 'The system will use the order settings used when the order place placed originally.'
        : '',
    ];

    const componentProps: ISharedModalBasic = {
      buttonAccept: buttonAcceptText !== '',
      buttonAcceptText,
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      buttonSecondary: buttonSecondaryText !== '',
      buttonSecondaryText,
      modalTitle: title,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading,
      contextDescription: [
        'The following error has been received:',
        Ascii.EmptySpace,
        ...errorDisplay,
        ...additionalText,
        ...retryText,
      ],
    };

    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css'],
    });
    await modalController.present();
    const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await modalController.onDidDismiss();
    if (modalResponse?.data?.buttonPressed === 'button-accept' && canRetryOrder) {
      this.retryOrder(order, options);
      return;
    }
    if (modalResponse?.data?.buttonPressed === 'button-secondary' && canRetryWithoutFailedItems) {
      void this.confirmAndRetryOrderWithoutFailedItems(
        order,
        options ?? {},
        failingItemCodes,
        buildAutoOrderNotificationItemErrorEntries(
          resolveAutoOrderNotificationItemErrors(notification, order),
        ),
      );
    }
  }

  getPrimaryFailureType(order: IAutoOrderOrder): AutoOrderFailureType {
    if (order.status === 'FAILED') {
      return 'STATUS';
    }
    if (order.iqStatus === 'FAILED') {
      return 'IQ';
    }
    if (order.pdfStatus === 'FAILED') {
      return 'PDF';
    }
    if (order.emailStatus === 'FAILED') {
      return 'EMAIL';
    }
    return 'STATUS';
  }

  triggerAction(
    autoOrder: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
    selectedStore: IStore,
  ): void {
    switch (autoOrder[statusProperty]) {
      case 'READY':
        break;
      case 'FAILED':
        this.store.dispatch(setSelectedStore({selectedStore: selectedStore}));
        this.store.dispatch(setNavigation({
          feature: NavigationPageEnum.AutoOrdering,
          navigation: {
            currentPage: NavigationPageEnum.AutoOrdering,
            currentTab: NavigationTabEnum.Orders,
          },
        }));
        this.store.dispatch(setGlobalModalVisibility({visibility: false, modalType: ['Notifications']}));
        setTimeout(() => {
          this.store.dispatch(getAutoOrderOrders({
            firebaseQueries: [{q: 'where', p: ['status', 'not-in', ['SUCCESS']]}],
            statusChip: {
              open_orders: false,
              prepared: false,
              completed: false,
              failed: true,
            },
          }));
        }, 2000);
        this.store.dispatch(setAutoOrderChipStatus({
          chips: {
            open_orders: false,
            prepared: false,
            completed: false,
            failed: true,
          },
        }));
        break;
      case 'SUCCESS':
        break;
      default:
        break;
    }

  }

  private mapStatusPropertyToFailureType(
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): AutoOrderFailureType | null {
    switch (statusProperty) {
      case 'emailStatus':
        return 'EMAIL';
      case 'pdfStatus':
        return 'PDF';
      case 'iqStatus':
        return 'IQ';
      case 'status':
        return 'STATUS';
      default:
        return null;
    }
  }
}

